import Vue from 'vue'
import Raven from 'raven-js'
import Push from '@/api/push'
import Call from '@/api/call'
import ravenSendData from '@/raven-send-data'
import config from '@/config'
import { versionCompare, sleep } from '@/utils/helpers'
import store from '@/store'
import { platformIsAndroid, platformIsIOS } from '@/cordova'
import Queue from '@/utils/Queue'

export enum PNM {
  EndCallFromSystem = 0, // システムからの通話終了通知
  Notice = 1, // お知らせ通知
  IncomingCall = 2, // 着信通知
  ReceivedChat = 3, // チャット受信通知
  EndIncomingCall = 4, // 着信終了通知
  OpenBrowser = 5, // ブラウザ起動通知
  ForceStart = 6, // アプリ強制起動(Android 用)
  ReceivedAlreadyRead = 7, // 既読通知
  PaymentCompleted = 8, // 決済完了通知
  TimelineIine = 9 // タイムラインのいいね通知
}

const appVersion = (): string => store.getters['AppInfo/appVersion']

export default {
  pushNotification: null,
  rootVue: null,
  calledPushIds: {},
  queue: null,

  /**
   * プッシュ通知を許可済みなら初期化する
   * 着信時には Vue の初期化より先に Puhs 通知を初期化したいので同期的に処理を実行する
   */
  async mightInitPushNotification(rootVue: Vue): Promise<any> {
    const isEnabled = await this.hasPermissionPushNotification()
    if (isEnabled) {
      await this.initPushNotification(rootVue)
    }
    // ios9 最新バージョン 9.3.5 より上なら iOS10 と判断
    if (platformIsIOS && versionCompare(device.version, '9.3.5')) {
      await this.initVoIPPushNotification(rootVue)
    }
    return
  },

  /**
   * プッシュ通知許可確認
   */
  hasPermissionPushNotification(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      PushNotification.hasPermission(data => {
        resolve(data.isEnabled)
      }, reject)
    })
  },

  /**
   * プッシュ通知の初期化処理
   */
  initPushNotification(rootVue: Vue) {
    this.rootVue = rootVue

    if (!this.queue) {
      this.queue = new Queue()
      this.rootVue.$on('resume', this.handleResume.bind(this))
    }

    if (platformIsAndroid) {
      PushNotification.createChannel(
        () => {
          console.log('[createChannel] Success')
        },
        err => {
          console.error('[createChannel] Error', err)
        },
        {
          id: config.NOTIFICATION_CHANNEL_ID,
          description: 'Default channel',
          importance: 5
        }
      )
    }

    const initOptions = {
      android: {
        clearBadge: true,
        icon: 'ic_launcher',
        iconColor: config.MAIN_COLOR,
        senderID: '644413767218',
        vibrate: true
      },
      ios: {
        clearBadge: true,
        alert: true,
        badge: true,
        sound: true
      }
    }
    this.pushNotification = PushNotification.init(initOptions)
    this.pushNotification.clearAllNotifications(
      () => {
        console.log('[clearAllNotifications] Success')
      },
      err => {
        console.log('[clearAllNotifications] Error', err)
      }
    )
    this.pushNotification.on('registration', this.handleRegistration.bind(this))
    this.pushNotification.on('notification', this.handleNotification.bind(this))
    this.pushNotification.on('error', this.handleError.bind(this))
    this.pushNotification.finish(
      () => {
        console.log('[pushNotification.finish] Success')
      },
      err => {
        console.error('[pushNotification.finish] Error', err)
      }
    )
  },

  /**
   * VoIP Push の初期化
   */
  initVoIPPushNotification(rootVue: Vue) {
    if (!platformIsIOS) {
      return
    }
    if (!this.rootVue) {
      this.rootVue = rootVue
    }
    const push = window.VoIPPushNotification.init()
    push.on('registration', this.handleVoIPRegistration.bind(this))
    push.on('notification', this.handleVoIPNotification.bind(this))
    push.on('error', this.handleVoIPError.bind(this))
  },

  /**
   * デバイストークンの登録処理
   */
  async handleRegistration(data) {
    console.log('[handleRegistration]: ' + JSON.stringify(data))
    if (data && data.registrationId) {
      await Push.postDeviceToken({
        device_token: data.registrationId,
        os: cordova.platformId,
        version: device.version,
        uuid: device.uuid,
        app_version: appVersion()
      }).catch(err => {
        console.error(err)
      })
      store.commit('MyData/setDeviceToken', data.registrationId)
    }
  },

  /**
   * プッシュ通知の受信処理
   */
  async handleNotification(data) {
    const notifyData = { message: data.message }
    const keys = Object.keys(data.additionalData)

    if (platformIsIOS) {
      for (let i = 0; i < keys.length; i++) {
        notifyData[keys[i].replace('gcm.notification.', '')] = data.additionalData[keys[i]]
      }
    } else {
      for (let i = 0; i < keys.length; i++) {
        notifyData[keys[i]] = data.additionalData[keys[i]]
      }
    }

    const pushId = notifyData['push_id']
    const isForeground = data.additionalData.foreground
    const isTappedNotification = this.isTappedNotification(
      data.additionalData.coldstart,
      isForeground
    )
    sessionStorage.setItem('isTappedNotification', isTappedNotification ? '1' : '0')

    // 'content-available' が指定されている通知は2回呼び出されることがる
    if (this.calledPushIds[pushId]) {
      // iOS && foreground なら2回目を無視
      if (platformIsIOS && isForeground) {
        return
      }
    } else {
      this.calledPushIds[pushId] = true
      // Android && !isForeground && !forceStart なら1回目を無視
      if (
        !isForeground &&
        platformIsAndroid &&
        notifyData['force-start'] !== '1' &&
        this.rootVue.$route.name !== 'chat'
      ) {
        return
      }
    }

    // バッググランドから不在着信以外の通知タップでチャット画面に遷移
    const shouldRouterReplace = this.rootVue.$route.name !== 'call' && isTappedNotification
    // iOS でロック中に非同期処理を行おうとするとエラーが発生するので Queue に追加して resume のタイミングで実行する
    const shouldEnqueue =
      platformIsIOS && isTappedNotification && sessionStorage.getItem('isActiveApp') !== '1'

    console.log('[handleNotification] notifyData: ' + JSON.stringify(notifyData))
    console.log('[handleNotification] isTappedNotification', isTappedNotification)
    console.log('[handleNotification] shouldRouterReplace', shouldRouterReplace)
    console.log('[handleNotification] shouldEnqueue', shouldEnqueue)

    let pnm =
      typeof notifyData['pnm'] === 'string' ? parseInt(notifyData['pnm'], 10) : notifyData['pnm']
    switch (pnm) {
      case PNM.EndCallFromSystem: {
        this.rootVue.$emit('endCallFromSystem')
        break
      }
      case PNM.Notice: {
        if (shouldEnqueue) {
          this.queue.enqueue(this.handlePNMNotice.bind(this, notifyData, shouldRouterReplace))
          return
        } else {
          this.handlePNMNotice(notifyData, shouldRouterReplace)
        }
        this.rootVue.$emit('receivedNotice', notifyData)
        break
      }
      case PNM.IncomingCall: {
        if (data && data.image) {
          notifyData['image'] = data.image
        }
        handleIncomingCall(data, notifyData, this.rootVue)
        break
      }
      case PNM.ReceivedChat: {
        if (shouldEnqueue) {
          this.queue.enqueue(this.handlePNMReceivedChat.bind(this, notifyData, shouldRouterReplace))
          return
        } else {
          this.handlePNMReceivedChat(notifyData, shouldRouterReplace)
        }
        this.rootVue.$emit('receivedChat', notifyData)
        break
      }
      case PNM.EndIncomingCall: {
        break
      }
      case PNM.OpenBrowser: {
        break
      }
      case PNM.ReceivedAlreadyRead: {
        let keys = notifyData['readed_message_timestamps']
        if (typeof notifyData['readed_message_timestamps'] === 'string') {
          keys = JSON.parse(notifyData['readed_message_timestamps'])
        }
        this.rootVue.$emit('receivedAlreadyRead', keys)
        break
      }
      case PNM.ForceStart: {
        sessionStorage.setItem('foreground', isForeground)
        activeAndroidApp()
        break
      }
      case PNM.PaymentCompleted: {
        const adjustEvent = new window.AdjustEvent(config.ADJUST_EVENT_PAYMENT)
        adjustEvent.addCallbackParameter('price', notifyData['price'])
        adjustEvent.addCallbackParameter('point', notifyData['point'])
        window.Adjust.trackEvent(adjustEvent)
        // TODO: アプリ側が PaymentCompleted を受け取ったよ通知をサーバに送るまでサーバはこの通知を送り続ける
        break
      }
      case PNM.TimelineIine: {
        this.rootVue.$emit('receivedTimelineIine', notifyData)
        break
      }
      default: {
        break
      }
    }
  },

  /**
   * エラー処理
   */
  handleError(err) {
    Raven.setTagsContext(ravenSendData.tagsContext)
    Raven.setUserContext(ravenSendData.userContext)
    Raven.captureException(err)
  },

  /**
   * voip push のデバイストークン登録
   */
  handleVoIPRegistration(data) {
    console.log('[handleVoIPRegistration] ' + JSON.stringify(data))
    if (data.deviceToken) {
      Push.postDeviceToken({
        voip_device_token: data.deviceToken,
        os: cordova.platformId,
        version: device.version,
        uuid: device.uuid,
        app_version: appVersion()
      }).catch(err => {
        console.error(err)
      })
      store.commit('MyData/setVoipDeviceToken', data.deviceToken)
    }
  },

  /**
   * voip push の受信処理
   */
  handleVoIPNotification(data) {
    const payload = JSON.parse(data.function)
    let pnm = typeof payload.pnm === 'string' ? parseInt(payload.pnm, 10) : payload.pnm
    switch (pnm) {
      case PNM.EndCallFromSystem: {
        this.rootVue.$emit('endCallFromSystem')
        break
      }
      case PNM.IncomingCall: {
        handleIncomingCall(data, payload, this.rootVue)
        break
      }
      default: {
        break
      }
    }
  },

  /**
   * voip push のエラー処理
   */
  handleVoIPError(err) {
    console.error('[voip notification]', err)
  },

  /**
   * cordova の  resume イベント
   */
  handleResume() {
    // Queue に入っている処理を実行する
    if (this.queue.size > 0) {
      this.queue.start()
    }
  },

  /**
   * PNM.Notice を受信した際の処理
   */
  handlePNMNotice(notifyData, shouldRouterReplace) {
    if (shouldRouterReplace) {
      let type = ''
      if (notifyData['type'] === '0') {
        type = 'syslog'
      } else if (notifyData['type'] === '1') {
        type = 'news'
      }
      this.rootVue.$router.replace(`/notice/${type}/${notifyData['no']}`)
    }
  },

  /**
   *
   */
  handlePNMReceivedChat(notifyData, shouldRouterReplace) {
    if (typeof notifyData['entries'] === 'string') {
      notifyData['entries'] = JSON.parse(notifyData['entries'])
    }

    if (shouldRouterReplace) {
      // replace しないとスワイプバックしたときに A の画面が表示されてしまう
      this.rootVue.$router.replace(`/message/${notifyData['user_id']}/push`)
      sessionStorage.setItem('isColdStart', '1')
    }
  },

  /**
   * 通知をタップしてアプリを起動した
   */
  isTappedNotification(coldstart: boolean, foreground: boolean): boolean {
    const documentHasFocus = document.hasFocus()
    const isActiveApp = sessionStorage.getItem('isActiveApp') === '1'

    if (platformIsAndroid) {
      return (
        // 非アクティブで通知タップ
        (documentHasFocus && !isActiveApp && !coldstart && !foreground) ||
        // 未起動で通知タップ
        (documentHasFocus && isActiveApp && coldstart && !foreground)
      )
    } else if (platformIsIOS) {
      return (
        // アプリ非アクティブで通知タップ
        (documentHasFocus && !coldstart && !foreground) ||
        // アプリ未起動で通知タップ
        (documentHasFocus && isActiveApp && coldstart)
        // all false だとアイコンタップで起動してもトークページにジャンプしてしまう
        // トーク画面でロック　→　通知タップで起動
        // (!documentHasFocus && !isActiveApp && !coldstart && !foreground)
      )
    }
    return false
  }
}

/**
 * 着信処理
 */
const handleIncomingCall = async (data: any, notifyData: any, rootVue: Vue) => {
  console.log('[handleIncomingCall] data: ' + JSON.stringify(data))
  console.log('[handleIncomingCall] notifyData: ' + JSON.stringify(notifyData))

  if (sessionStorage.getItem('isCalling') === '1') {
    console.log(`No.${notifyData.log_no} 通話中なので無視`)
    return
  }

  // 終了済みの着信に反応してしまわないように着信中チェック
  try {
    const { incoming_call } = await Call.checkIncomingCall({
      log_no: notifyData.log_no
    })
    if (!incoming_call) {
      console.log(`No.${notifyData.log_no} 終了済みの通話なので無視`)
      return
    }
  } catch (err) {
    console.error(err)
  }

  const params = {
    type: notifyData.type,
    log_no: notifyData.log_no,
    user_id: notifyData.user_id,
    name: notifyData.name,
    profile_image_url: notifyData.image,
    api_key: notifyData.api_key,
    session_id: notifyData.session_id,
    token: notifyData.token,
    can_phone_call_time: notifyData.can_phone_call_time
  }

  if (platformIsAndroid) {
    activeAndroidApp()
    // Android なら EndIncomingCall で先に起動しているので正しい foreground の値がとれない対策
    params['foreground'] = sessionStorage.getItem('foreground') || data.additionalData.foreground
  } else if (platformIsIOS) {
    params['voip'] = '1'
  }

  rootVue.$router.push({
    name: 'call',
    params: params
  })

  // CordovaCall の video かどうかの設定や着信処理は遷移してから実行する必要がある？(ごめんなさい理由を忘れました)
  if (platformIsIOS) {
    if (notifyData.type === 'video' || notifyData.type === 'nozoki') {
      cordova.plugins.CordovaCall.setVideo(true)
    } else {
      cordova.plugins.CordovaCall.setVideo(false)
    }
    cordova.plugins.CordovaCall.receiveCall(notifyData.name)
  }
}

/**
 * Android 端末でアプリをアクティブ状態にする
 */
const activeAndroidApp = () => {
  if (!platformIsAndroid) {
    return
  }
  cordova.plugins.backgroundMode.unlock()
  cordova.plugins.backgroundMode.moveToForeground()
  cordova.plugins.MoveTask.toFront(
    () => {
      console.log('[MoveTask toFront] Success')
    },
    err => {
      console.error('[MoveTask toFront] Error', err)
    }
  )
}
